import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Chat } from "../../components/Chat";
import { Card } from "../../components/Card";
import styles from './chat.module.css'
import clsx from 'clsx';
import { Layout } from "../../components/Layout/Layout";
import { PillButton } from "../../components/PillButton";
import { ReactComponent as PaperClip } from '../../assets/PaperClip.svg'
import { ReactComponent as Database } from '../../assets/Database.svg'
import { ReactComponent as Code } from '../../assets/Code.svg'
import { ReactComponent as DownloadFile } from '../../assets/DownloadFile.svg'
import { ReactComponent as Close } from '../../assets/Close.svg'
import { ReactComponent as SendIcon } from '../../assets/SendIcon.svg'
import { getApiUrl } from '../../config/env'

const lorem = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum vel nulla ultricies, pulvinar ex a, pharetra velit. Phasellus tempus augue ac lacus interdum molestie. Proin eget malesuada quam. In tempus auctor sodales. Nam posuere, ipsum eleifend scelerisque fermentum, neque augue egestas diam, in fermentum nunc sem et nisi. Fusce pharetra ultrices ligula a convallis. Etiam a elementum mi, non molestie mauris. Fusce et tortor interdum, dictum dui vulputate, iaculis turpis. Mauris a metus sed tortor pretium condimentum ut quis leo. Nam feugiat consectetur dolor et facilisis. In risus libero, tincidunt vitae elit sit amet, venenatis suscipit enim.'

interface StreamResponse {
    content?: string;
    error?: string;
}

const ChatPage = () => {

    const [chatHistory, setChatHistory] = useState<
        { sender: "me" | "ai"; message: string, images: { url: string, name: string }[] }[]
    >([]);
    const [aiIsTyping, setAiIsTyping] = useState(false);

    const handleFormulationStream = async (message: string) => {
        try {
            const response = await fetch(getApiUrl('/formulation/generate'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    nlp_problem: message
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Create a new message immediately for streaming
            setChatHistory(prev => [...prev, { sender: "me", message, images: [] }]);

            let streamingMessage = "";
            setChatHistory(prev => [...prev, { sender: "ai", message: "", images: [] }]);

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();

            if (!reader) {
                throw new Error('Response body is null');
            }

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data: StreamResponse = JSON.parse(line.slice(6));

                            if (data.error) {
                                throw new Error(data.error);
                            }

                            if (data.content) {
                                streamingMessage += data.content;
                                // Update the last message (AI's message) with the accumulated content
                                setChatHistory(prev => {
                                    const newHistory = [...prev];
                                    newHistory[newHistory.length - 1] = {
                                        sender: "ai",
                                        message: streamingMessage,
                                        images: []
                                    };
                                    return newHistory;
                                });
                            }
                        } catch (e) {
                            console.error('Error parsing SSE data:', e);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error in streaming:', error);
            // Handle error in UI
        } finally {
            setAiIsTyping(false);
        }
    };

    const addMessage = async ({ text, images }: { text: string, images: { url: string, name: string }[] }) => {
        setAiIsTyping(true);
        try {
            await handleFormulationStream(text);
        } catch (error) {
            console.error('Error getting AI response:', error);
            // Optionally handle error in UI
        } finally {
            setAiIsTyping(false);
        }
    };

    return (
        <Layout>
            <div className={styles.chatContent}>
                {chatHistory.map(({ sender, message, images }) => (
                    <Card className={clsx(sender === 'me' ? styles.messageMe : styles.messageOther)}>
                        <p className={clsx(
                            styles.messageText,
                            sender === 'me' ? styles.messageTextMe : styles.messageTextOther
                        )}>{message}</p>
                        {images.map(({ url, name }) => (
                            <div className={styles.imageContainer}>
                                <p className={styles.imageName}>{name}</p>
                                <img className={styles.image} src={url} alt={name} />
                            </div>
                        ))}
                    </Card>
                ))}
            </div>
            <footer className={styles.footer}>
                <div className={styles.footerContent}>
                    <Chat onSubmit={addMessage} onChange={(e) => { console.log(e) }} >
                        <div className={styles.footer}>
                            <div className={styles.iconContainer}>
                                <PaperClip cursor='pointer' onClick={() => { console.log('Clicked') }} />
                                <Database cursor='pointer' />
                                <Code cursor='pointer' />
                            </div>
                            <div className={styles.divider} />
                            <div className={styles.iconContainer}>
                                <PillButton label="Context: from builder" prefix={<DownloadFile />} variant="primary" />
                                <PillButton label="Context: from builder" suffix={<Close />} variant="secondary" />
                                <div onClick={() => {
                                    console.log('Clicked')
                                }} style={{ cursor: 'pointer', height: '32px', width: '32px', backgroundColor: '#000000', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '10px', marginLeft: 'auto' }}>
                                    <SendIcon />
                                </div>
                            </div>
                        </div>
                    </Chat>
                </div>
            </footer>
        </Layout>
    );
};

export default ChatPage;
